import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';
import {
  addEntities,
  EntityState,
  removeEntity,
  setAllEntities,
  updateEntity,
  upsertEntity,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { ToastrErrorComponent } from '@shared/components/toastr/toastr-error.component';
import { toast } from 'ngx-sonner';
import { concatMap, pipe, switchMap } from 'rxjs';
import { v7 as uuidv7 } from 'uuid';

import { Production } from '../../référentiel-métier-processus/processus-aptitude/models/production.model';
import { ProductionCreateRequest } from '../requests/production-create-request.model';
import { ProductionUpdateRequest } from '../requests/production-update-request.model';
import { RessourcesOrdonneesRequest } from '../requests/ressources-ordonnées-request.model';
import { ProductionService } from '../services/production.service';

export function avecMethodesProductions() {
  return signalStoreFeature(
    { state: type<EntityState<Production>>() },
    // Updater
    withMethods((store) => ({
      chargerProductions: (state: { productions: Production[] }) =>
        patchState(store, setAllEntities(state.productions)),
      ajouterProduction: (state: { production: Production }) => patchState(store, upsertEntity(state.production)),
      ajouterProductions: (state: { productions: Production[] }) => patchState(store, addEntities(state.productions)),
      modifierProduction: (state: { production: Production }) =>
        patchState(store, updateEntity({ id: state.production.id, changes: state.production })),
      supprimerProduction: (idProduction: string) => patchState(store, removeEntity(idProduction)),
      ordonnerRessourcesLocalement: (state: { idProduction: string } & RessourcesOrdonneesRequest) => {
        const production = store.entityMap()[state.idProduction];
        if (!production) return;

        const autres = production.ressources.filter((r) => r.type !== state.type);
        const ordonnées = state.idsRessources
          .map((id, indice) => ({ ressource: production.ressources.find((r) => r.id === id), indice }))
          .filter((x) => x.ressource !== undefined)
          .map((x) => ({ ...x.ressource!, indice: x.indice }));

        patchState(store, updateEntity({ id: state.idProduction, changes: { ressources: [...autres, ...ordonnées] } }));
      },
    })),

    // Effects
    withMethods((store, productionService = inject(ProductionService)) => ({
      créer: rxMethod<{ data: ProductionCreateRequest; next: () => void; error: () => void }>(
        pipe(
          switchMap(({ data, next, error }) =>
            productionService.create(data).pipe(
              tapResponse({
                next: (result) => {
                  store.ajouterProductions({ productions: result });
                  next();
                },
                error: (err: HttpErrorResponse) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        err.error.detail ??
                        'Une erreur est survenue lors de la création des productions, merci de réessayer.',
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  error();
                },
              }),
            ),
          ),
        ),
      ),
      modifier: rxMethod<{
        idProduction: string;
        idCartographie: string;
        data: ProductionUpdateRequest;
        next: () => void;
        error?: () => void;
      }>(
        pipe(
          switchMap(({ idCartographie, idProduction, data, next, error }) =>
            productionService.update(idCartographie, idProduction, data).pipe(
              tapResponse({
                next: () => next(),
                error: (err: HttpErrorResponse) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        err.error.detail ??
                        'Une erreur est survenue lors de la modification de la production, merci de réessayer.',
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  error?.();
                },
              }),
            ),
          ),
        ),
      ),
      ordonnerRessources: rxMethod<
        { idProduction: string; next?: () => void; error?: () => void } & RessourcesOrdonneesRequest
      >(
        pipe(
          // concatMap : les réordonnancements successifs sont envoyés dans l'ordre où l'utilisateur les a faits.
          concatMap(({ idProduction, type, idsRessources, next, error }) => {
            store.ordonnerRessourcesLocalement({ idProduction, type, idsRessources });
            return productionService.ordonnerRessources(idProduction, { type, idsRessources }).pipe(
              tapResponse({
                next: () => next?.(),
                error: (err: HttpErrorResponse) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message:
                        err.error?.detail ??
                        'Une erreur est survenue lors du réordonnancement des ressources, merci de réessayer.',
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                  error?.();
                },
              }),
            );
          }),
        ),
      ),
      supprimer: rxMethod<{ idProduction: string; next: () => void }>(
        pipe(
          switchMap(({ idProduction: idSecteur, next }) =>
            productionService.supprimer(idSecteur).pipe(
              tapResponse({
                next: () => {
                  store.supprimerProduction(idSecteur);
                  next();
                },
                error: (err) => {
                  console.error(err);
                  const toastrId = uuidv7();
                  toast.custom(ToastrErrorComponent, {
                    componentProps: {
                      message: 'Une erreur est survenue lors de la suppression de la production, merci de réessayer.',
                      id: toastrId,
                    },
                    id: toastrId,
                  });
                },
              }),
            ),
          ),
        ),
      ),
      selection: rxMethod<string>(
        pipe(
          switchMap((id) => productionService.get(id)),
          tapResponse({
            next: (production) => store.ajouterProduction({ production }),
            error: (err) => {
              console.error(err);
              const toastrId = uuidv7();
              toast.custom(ToastrErrorComponent, {
                componentProps: {
                  message: `Un problème est survenu lors de la récupération de la production, contactez le support.`,
                  id: toastrId,
                },
                id: toastrId,
              });
            },
          }),
        ),
      ),
    })),
  );
}
